import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  TextField, 
  Button, 
  List, 
  ListItem, 
  ListItemText, 
  Divider, 
  CircularProgress,
  useTheme
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { processTaxQuery, getRecentQueries } from '../services/aiAssistantService';
import { AIAssistantQuery } from '../types';
import { isGenAIConfigured } from '../config/genai';

const AIAssistant: React.FC = () => {
  const theme = useTheme();
  const { currentUser } = useAuth();
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [recentQueries, setRecentQueries] = useState<AIAssistantQuery[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [apiConfigured, setApiConfigured] = useState(false);

  const loadRecentQueries = useCallback(async () => {
    if (!currentUser) return;
    
    try {
      const queries = await getRecentQueries(currentUser.uid);
      setRecentQueries(queries);
    } catch (error) {
      console.error("Error loading recent queries:", error);
      setError("Failed to load recent queries");
    }
  }, [currentUser]);

  useEffect(() => {
    // Check if the API is configured
    setApiConfigured(isGenAIConfigured());
    
    // Load recent queries if user is logged in
    if (currentUser) {
      loadRecentQueries();
    }
  }, [currentUser, loadRecentQueries]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!query.trim()) return;
    if (!currentUser) {
      setError('You must be logged in to use the AI Assistant');
      return;
    }
    if (!apiConfigured) {
      setError('AI Assistant is not configured. Please set up your API key.');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await processTaxQuery(currentUser.uid, query);
      
      // Add the new query to the recent queries list
      const newQuery: AIAssistantQuery = {
        id: Date.now().toString(), // Temporary ID until we get the real one from Firestore
        userId: currentUser.uid,
        query,
        response,
        createdAt: new Date()
      };
      
      setRecentQueries([newQuery, ...recentQueries]);
      setQuery('');
      
      // Reload recent queries to get the actual data from Firestore
      loadRecentQueries();
    } catch (error) {
      console.error('Error processing query:', error);
      if (error instanceof Error) {
        setError(`Failed to process query: ${error.message}`);
      } else {
        setError('Failed to process query');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 800, mx: 'auto', p: 2 }}>
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          Tax Assistant AI
        </Typography>
        
        {!apiConfigured && (
          <Typography color="error" sx={{ mb: 2 }}>
            AI Assistant is not configured. Please set up your Google Generative AI API key in the environment variables.
          </Typography>
        )}
        
        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}
        
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Ask a tax-related question"
            variant="outlined"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            disabled={loading || !currentUser || !apiConfigured}
            sx={{ mb: 2 }}
          />
          
          <Button
            type="submit"
            variant="contained"
            color="primary"
            endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
            disabled={loading || !query.trim() || !currentUser || !apiConfigured}
          >
            {loading ? 'Processing...' : 'Ask'}
          </Button>
        </form>
      </Paper>
      
      {recentQueries.length > 0 && (
        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Recent Queries
          </Typography>
          
          <List>
            {recentQueries.map((item, index) => (
              <React.Fragment key={item.id}>
                <ListItem alignItems="flex-start">
                  <ListItemText
                    primary={
                      <Typography 
                        component="div" 
                        variant="subtitle1" 
                        sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}
                      >
                        Q: {item.query}
                      </Typography>
                    }
                    secondary={
                      <Typography 
                        component="div" 
                        variant="body2" 
                        sx={{ mt: 1, whiteSpace: 'pre-wrap' }}
                      >
                        A: {item.response}
                      </Typography>
                    }
                  />
                </ListItem>
                {index < recentQueries.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );
};

export default AIAssistant; 